// LUMINA · pages/vajra/components/SanghaBoard.jsx
// Redesigned: visitor-facing time-grouped event list with streamlined cards

import React, { useState, useEffect, useMemo } from 'react';
import { API, GD, DG, PANEL } from '../constants';
import { feeTag } from '../utils';
import { PaymentInfo } from '../primitives';
import EventDetailModal from './EventDetailModal';

// ── Date helpers ────────────────────────────────────────────────────────────
function parseDate(s) { if (!s) return null; const p=s.split('-');if(p.length!==3)return null;return new Date(+p[0],+p[1]-1,+p[2],12,0,0); }
function dateStr(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
function todayStr() { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
function mondayOfWeek(d) { const day=d.getDay(); const mon=new Date(d); mon.setDate(d.getDate()-(day===0?6:day-1)); return mon; }

const WD_ZH = ["周日","周一","周二","周三","周四","周五","周六"];
function fmtDate(start, end) {
  if (!start) return "日期待定";
  const d = parseDate(start); if (!d) return start;
  const m = d.getMonth()+1, day = d.getDate(), wd = WD_ZH[d.getDay()];
  let s = `${m}月${day}日 ${wd}`;
  if (end && end !== start) { const de = parseDate(end); if (de) s += ` – ${de.getMonth()+1}月${de.getDate()}日`; }
  return s;
}
function fmtTime(st, et) { if (!st) return ""; let s=st.slice(0,5); if(et)s+="–"+et.slice(0,5); return s; }

// ── Session dates display ──────────────────────────────────────────────────
function getSessions(ev) {
  let s = ev.sessions;
  if (!s) return null;
  if (typeof s === 'string') { try { s = JSON.parse(s); } catch(e) { return null; } }
  if (!Array.isArray(s) || s.length === 0) return null;
  return s;
}
function fmtSessionDates(sessions, timeStr) {
  // sessions: [{date, time, title_zh, ...}]
  // extract unique dates in order, format compactly
  const dates = sessions.map(s => s.date).filter(Boolean);
  if (dates.length === 0) return null;
  // Compact: "6/20, 21, 27, 28"
  const groups = [];
  let curMonth = '', curDays = [];
  for (const d of dates) {
    const m = d.slice(5,7).replace(/^0/,''), day = d.slice(8).replace(/^0/,'');
    const mKey = m + '月';
    if (curMonth !== mKey) {
      if (curDays.length) groups.push({month:curMonth, days:curDays});
      curMonth = mKey; curDays = [day];
    } else {
      curDays.push(day);
    }
  }
  if (curDays.length) groups.push({month:curMonth, days:curDays});
  let out = groups.map(g => `${g.month}${g.days.join(', ')}日`).join(' · ');
  if (timeStr) out += ' · ' + timeStr;
  return out;
}

// ── Weekday only (for multi-session events, no single weekday makes sense) ──
function fmtDateSimple(start, end) {
  if (!start) return "日期待定";
  const d = parseDate(start); if (!d) return start;
  const m = d.getMonth()+1, day = d.getDate();
  let s = `${m}月${day}日`;
  if (end && end !== start) { const de = parseDate(end); if (de) s += ` – ${de.getMonth()+1}月${de.getDate()}日`; }
  return s;
}

const TYPE_MAP = { weekly_practice:"周共修",teaching:"开示",empowerment:"灌顶",retreat:"禅修营",puja:"法会",ritual:"法事",other:"活动" };
const TYPE_CLR = { weekly_practice:"#059669",teaching:"#3b82f6",empowerment:"#dc2626",retreat:"#8b5cf6",puja:"#d97706",ritual:"#10b981",other:"#6b7280" };

// ── Time grouping ───────────────────────────────────────────────────────────
function groupByTime(events) {
  const today = todayStr();
  const now = new Date();
  const mon = mondayOfWeek(now);
  const sun = new Date(mon); sun.setDate(mon.getDate()+6);
  const weekStart = dateStr(mon), weekEnd = dateStr(sun);
  const monthEnd = dateStr(new Date(now.getFullYear(), now.getMonth()+1, 0, 12,0,0));

  const thisWeek=[], thisMonth=[], later=[], past=[];
  for (const ev of events) {
    const s=ev.start_date, e=ev.end_date || s;
    if (e && e < today) { past.push(ev); continue; }
    if (s && s >= weekStart && s <= weekEnd) { thisWeek.push(ev); continue; }
    if (s && s < weekStart && e && e >= today) { thisWeek.push(ev); continue; }
    if (s && s <= monthEnd && s > weekEnd) { thisMonth.push(ev); continue; }
    later.push(ev);
  }
  const sortFn = (a,b) => {
    const da = (a.start_date||"").localeCompare(b.start_date||"");
    if (da !== 0) return da;
    return (a.start_time||"").localeCompare(b.start_time||"");
  };
  thisWeek.sort(sortFn); thisMonth.sort(sortFn); later.sort(sortFn);
  past.sort((a,b)=>(b.start_date||"").localeCompare(a.start_date||""));
  return { thisWeek, thisMonth, later, past };
}

// ── Section header ──────────────────────────────────────────────────────────
function SectionHeader({ icon, title, count }) {
  return (
    <div style={{ display:"flex",alignItems:"center",gap:10, padding:"14px 0", borderBottom:"1px solid rgba(212,175,55,0.2)", marginBottom:12, marginTop:4 }}>
      <span style={{ fontSize:18 }}>{icon}</span>
      <span style={{ fontSize:15,fontWeight:700,color:DG,letterSpacing:"0.04em" }}>{title}</span>
      <span style={{ fontSize:11,color:"#A88514",background:"rgba(212,175,55,0.1)",borderRadius:10,padding:"1px 8px",fontWeight:600 }}>{count}</span>
    </div>
  );
}

// ── Event card ──────────────────────────────────────────────────────────────
function EventCard({ ev, onExpand, isExpanded, onQuickLog, onRegister, onClick }) {
  const typeLabel = TYPE_MAP[ev.event_type] || "活动";
  const typeColor = TYPE_CLR[ev.event_type] || "#6b7280";
  const spotsLeft = ev.capacity ? ev.capacity - (ev.registrant_count||0) : null;
  const ft = feeTag(ev);
  const sessions = getSessions(ev);
  const timeStr = sessions ? (sessions[0]?.time || ev.start_time) : ev.start_time;
  const timeDisplay = fmtTime(timeStr, sessions ? null : ev.end_time);
  const dateDisplay = sessions
    ? fmtSessionDates(sessions, null)
    : fmtDate(ev.start_date, ev.end_date);
  const isSeries = !!sessions;
  const sessCountLabel = isSeries ? (() => {
    const lessons = sessions.filter(s => (s.title_zh || '').includes('课'));
    const emps = sessions.filter(s => (s.title_zh || '').includes('灌顶') || (s.title_en || '').toLowerCase().includes('empowerment'));
    const other = sessions.length - lessons.length - emps.length;
    const p = [];
    if (lessons.length) p.push(`${lessons.length}节课`);
    if (emps.length) p.push(`${emps.length}灌顶`);
    if (other > 0) p.push(`${other}场`);
    return p.join('+');
  })() : '';

  return (
    <div style={{ background:PANEL, border:"1px solid rgba(6,78,59,0.1)", borderRadius:12, marginBottom:8, overflow:"hidden" }}>

      {/* ── Main row ──────────────────────────────────────────────────── */}
      <div
        onClick={() => onClick ? onClick(ev) : onExpand(ev.id)}
        style={{ display:"flex",alignItems:"center",gap:14,padding:"14px 18px",cursor:"pointer",
          transition:"background .15s" }}
        onMouseEnter={e=>e.currentTarget.style.background="rgba(6,78,59,0.015)"}
        onMouseLeave={e=>e.currentTarget.style.background="transparent"}
      >
        {/* Date badge */}
        <div style={{ minWidth:52,textAlign:"center",padding:"8px 4px",borderRadius:8,background:"rgba(6,78,59,0.06)" }}>
          <div style={{ fontSize:18,fontWeight:700,color:DG,lineHeight:1 }}>
            {ev.start_date ? ev.start_date.slice(8).replace(/^0/,"") : "?"}
          </div>
          <div style={{ fontSize:10,color:"#999",marginTop:1 }}>
            {ev.start_date ? ev.start_date.slice(5,7)+"月" : ""}
          </div>
        </div>

        {/* Info */}
        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ fontSize:15,fontWeight:600,color:"rgba(0,0,0,0.82)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginBottom:3 }}>
            {ev.title_zh || ev.title_en}
          </div>
          <div style={{ fontSize:12,color:"#6b7280",marginBottom:2 }}>
            {dateDisplay}
            {timeDisplay && <span style={{ marginLeft:6,color:"#9ca3af" }}>{timeDisplay}</span>}
            {sessCountLabel && <span style={{ marginLeft:6,fontSize:10,color:"#D4AF37",background:"rgba(212,175,55,0.1)",borderRadius:6,padding:"0 6px" }}>{sessCountLabel}</span>}
          </div>
          <div style={{ fontSize:12,color:DG,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
            {(ev.teacher_zh||ev.teacher_en) && <span>🪷 {ev.teacher_zh||ev.teacher_en}</span>}
            {(ev.teacher_zh||ev.teacher_en) && ev.location && <span style={{margin:"0 6px",color:"#ccc"}}>·</span>}
            {ev.location && <span>{ev.location}</span>}
            {!ev.teacher_zh && !ev.teacher_en && !ev.location && ev.online_link && <span>💻 线上活动</span>}
          </div>
        </div>

        {/* Tags + actions */}
        <div style={{ display:"flex",flexDirection:"column",alignItems:"flex-end",gap:5,flexShrink:0 }}>
          <div style={{ display:"flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end" }}>
            <span style={{ fontSize:10,padding:"1px 7px",borderRadius:8,background:typeColor+"18",color:typeColor,fontWeight:600 }}>{typeLabel}</span>
            {ft && <span style={{ fontSize:10,padding:"1px 7px",borderRadius:8,background:ft.bg,color:ft.color,border:`1px solid ${ft.border}`,fontWeight:500 }}>{ft.label}</span>}
            {spotsLeft !== null && <span style={{ fontSize:10,padding:"1px 7px",borderRadius:8,color:spotsLeft>0?"#888":"#ef4444",background:spotsLeft>0?"rgba(0,0,0,0.04)":"rgba(239,68,68,0.08)" }}>{spotsLeft>0?`余${spotsLeft}`:"满"}</span>}
          </div>
          <div style={{ display:"flex",gap:6 }}>
            {ev.recommended_practice && (
              <button onClick={e=>{e.stopPropagation();onQuickLog(ev);}}
                style={{ fontSize:10,padding:"2px 10px",borderRadius:8,background:"rgba(6,78,59,0.07)",border:`1px solid rgba(6,78,59,0.2)`,color:DG,cursor:"pointer",whiteSpace:"nowrap" }}>☸ 计数</button>
            )}
            {ev.registration_required && spotsLeft !== 0 && (
              <button onClick={e=>{e.stopPropagation();onRegister(ev);}}
                style={{ fontSize:10,padding:"2px 10px",borderRadius:8,background:DG,color:"#fff",border:"none",cursor:"pointer",whiteSpace:"nowrap",fontWeight:600 }}>报名</button>
            )}
            <span style={{ color:DG,fontSize:12,lineHeight:"22px" }}>{isExpanded ? "▲" : "▼"}</span>
          </div>
        </div>
      </div>

      {/* ── Expanded sessions ─────────────────────────────────────────── */}
      {isExpanded && (
        <div style={{ padding:"0 18px 16px",marginLeft:66,borderTop:"1px solid rgba(6,78,59,0.07)",paddingTop:12 }}>
          {(()=>{let s=ev.sessions;if(typeof s==='string')try{s=JSON.parse(s)}catch(e){s=[]};return Array.isArray(s)?s:[];})().map((s,i)=>(
            <div key={i} style={{ fontSize:13,color:"#666",marginBottom:6,display:"flex",gap:12 }}>
              {s.date&&<span style={{ color:DG,minWidth:80 }}>{s.date}</span>}
              <span>{s.title_zh||s.title||""}{s.time?` · ${s.time}`:""}</span>
            </div>
          ))}
          {ev.online_link && <a href={ev.online_link} target="_blank" rel="noopener noreferrer"
            style={{ display:"inline-block",marginTop:6,fontSize:12,color:"#6366f1",textDecoration:"underline" }}
            onClick={e=>e.stopPropagation()}>🔗 进入线上活动</a>}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function SanghaBoard({ token }) {
  const [events, setEvents] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [quickLog, setQuickLog] = useState(null);
  const [quickMsg, setQuickMsg] = useState("");
  const [regModal, setRegModal] = useState(null);
  const [regWechat, setRegWechat] = useState("");
  const [regWhatsapp, setRegWhatsapp] = useState("");
  const [regLoading, setRegLoading] = useState(false);
  const [regErr, setRegErr] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [showAllPast, setShowAllPast] = useState(false);
  const PAST_PREVIEW = 5;

  const load = () => {
    const h = {"Authorization":`Bearer ${token}`};
    fetch(`${API}/vajra/events/public?include_schedules=true`,{headers:h}).then(r=>r.ok?r.json():[]).then(setEvents).catch(()=>{});
  };
  useEffect(()=>{ load(); },[]);

  const { thisWeek, thisMonth, later, past } = useMemo(() => groupByTime(events), [events]);

  const submitQuickLog = async (e) => {
    e.preventDefault();
    if (!quickLog) return;
    const h = {"Authorization":`Bearer ${token}`,"Content-Type":"application/json"};
    const body = { practice_type: quickLog.practice_type, count: parseInt(quickLog.count)||0, duration_min:0,
      logged_date: new Date().toISOString().slice(0,10), event_id: quickLog.event_id };
    const r = await fetch(`${API}/vajra/practice`,{method:"POST",headers:h,body:JSON.stringify(body)});
    if(r.ok){setQuickMsg("✓ 已记录");setQuickLog(null);}
    else setQuickMsg("✗ 记录失败");
  };

  const handleQuickLog = (ev) => { setQuickLog({event_id:ev.id,practice_type:ev.recommended_practice,count:"108"}); setQuickMsg(""); };
  const handleRegister = (ev) => { setRegModal({eventId:ev.id,eventTitle:ev.title_zh||ev.title_en||"法会活动",donationType:ev.donation_type||"free"}); };

  const hasContent = thisWeek.length + thisMonth.length + later.length > 0;

  return (
    <div style={{padding:"12px 0"}}>

      {/* Empty state */}
      {events.length === 0 && (
        <div style={{textAlign:"center",padding:"40px 0",color:"#bbb",fontSize:14}}>近期暂无法讯，请关注更新</div>
      )}

      {/* ── Time-grouped sections ──────────────────────────────────────── */}
      {thisWeek.length > 0 && (
        <div style={{marginBottom:8}}>
          <SectionHeader icon="📍" title="本周法会" count={thisWeek.length} />
          {thisWeek.map(ev => (
            <EventCard key={ev.id} ev={ev}
              onExpand={id=>setExpanded(expanded===id?null:id)}
              isExpanded={expanded===ev.id}
              onQuickLog={handleQuickLog}
              onRegister={handleRegister}
              onClick={setSelectedEvent}
            />
          ))}
        </div>
      )}

      {thisMonth.length > 0 && (
        <div style={{marginBottom:8}}>
          <SectionHeader icon="📅" title="本月法会" count={thisMonth.length} />
          {thisMonth.map(ev => (
            <EventCard key={ev.id} ev={ev}
              onExpand={id=>setExpanded(expanded===id?null:id)}
              isExpanded={expanded===ev.id}
              onQuickLog={handleQuickLog}
              onRegister={handleRegister}
              onClick={setSelectedEvent}
            />
          ))}
        </div>
      )}

      {later.length > 0 && (
        <div style={{marginBottom:8}}>
          <SectionHeader icon="🔭" title="后续活动" count={later.length} />
          {later.map(ev => (
            <EventCard key={ev.id} ev={ev}
              onExpand={id=>setExpanded(expanded===id?null:id)}
              isExpanded={expanded===ev.id}
              onQuickLog={handleQuickLog}
              onRegister={handleRegister}
              onClick={setSelectedEvent}
            />
          ))}
        </div>
      )}

      {/* Past events */}
      {past.length > 0 && (
        <div style={{marginTop:24}}>
          <button onClick={()=>setShowPast(s=>!s)} style={{
            display:"flex",alignItems:"center",gap:8, background:"none",border:"none",cursor:"pointer",
            color:"#9ca3af",fontSize:13,padding:"8px 0",
          }}>
            <span style={{ display:"inline-block",transform:showPast?"rotate(90deg)":"rotate(0deg)",transition:"transform .2s",fontSize:10 }}>▶</span>
            已结束的活动 ({past.length})
          </button>
          {showPast && (
            <div style={{marginTop:8}}>
              {(showAllPast ? past : past.slice(0, PAST_PREVIEW)).map(ev => (
                <EventCard key={ev.id} ev={ev}
                  onExpand={id=>setExpanded(expanded===id?null:id)}
                  isExpanded={expanded===ev.id}
                  onQuickLog={handleQuickLog}
                  onRegister={handleRegister}
                  onClick={setSelectedEvent}
                />
              ))}
              {past.length > PAST_PREVIEW && !showAllPast && (
                <button onClick={()=>setShowAllPast(true)} style={{
                  display:"block",width:"100%",padding:"10px",borderRadius:8,
                  background:"rgba(0,0,0,0.02)",border:"1px dashed rgba(0,0,0,0.1)",
                  color:"#999",fontSize:12,cursor:"pointer",marginTop:4,
                }}>
                  显示全部 {past.length} 个往期活动 ↓
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Fixed position elements ────────────────────────────────────── */}

      {/* Registration message toast */}
      {quickMsg && quickMsg.includes("报名") && (
        <div style={{position:"fixed",bottom:20,right:20,zIndex:200,background:"#fff",border:`1px solid rgba(6,78,59,0.2)`,borderRadius:10,padding:"12px 18px",boxShadow:"0 4px 16px rgba(0,0,0,0.1)",fontSize:13,color:quickMsg.startsWith("✓")?DG:"#cc2200"}}>
          {quickMsg}
        </div>
      )}

      {/* Quick log panel */}
      {quickLog && (
        <div style={{position:"fixed",bottom:20,left:"50%",transform:"translateX(-50%)",zIndex:100,
          background:"#fff",border:`1px solid rgba(6,78,59,0.2)`,borderRadius:12,
          padding:"18px 22px",boxShadow:"0 8px 32px rgba(0,0,0,0.12)",minWidth:300,maxWidth:380}}>
          <div style={{fontSize:14,color:DG,marginBottom:10}}>☸ 一键记录 · {quickLog.practice_type}</div>
          <form onSubmit={submitQuickLog} style={{display:"flex",gap:8,alignItems:"center"}}>
            <input type="number" value={quickLog.count} onChange={e=>setQuickLog({...quickLog,count:e.target.value})}
              style={{width:80,padding:"7px 10px",border:"1px solid #ddd",borderRadius:6,fontSize:14,textAlign:"center"}}
              placeholder="数量"/>
            <span style={{fontSize:12,color:"#888"}}>遍</span>
            <button type="submit" style={{padding:"7px 16px",background:DG,color:"#fff",border:"none",borderRadius:6,fontSize:13,cursor:"pointer"}}>记录</button>
            <button type="button" onClick={()=>{setQuickLog(null);setQuickMsg("");}} style={{padding:"7px 10px",background:"transparent",border:"1px solid #ddd",borderRadius:6,fontSize:13,cursor:"pointer",color:"#aaa"}}>取消</button>
          </form>
          {quickMsg && <div style={{marginTop:8,fontSize:12,color:quickMsg.startsWith("✓")?DG:"#cc2200"}}>{quickMsg}</div>}
        </div>
      )}

      {/* Registration modal */}
      {regModal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:1000,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
          <div style={{background:"#fff",borderRadius:16,padding:"32px 28px",
            maxWidth:400,width:"90%",boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}}>
            <div style={{fontSize:16,fontWeight:600,marginBottom:4,color:"#1a1a1a"}}>
              {regModal.eventTitle}
            </div>
            <div style={{fontSize:12,color:"#888",marginBottom:20}}>
              请留下联系方式（可选）以便我们通知活动详情
            </div>
            <input value={regWechat} onChange={e=>setRegWechat(e.target.value)}
              placeholder="微信 WeChat ID（可选）"
              style={{width:"100%",padding:"10px 12px",border:"1px solid #e0e0e0",borderRadius:8,
                fontSize:13,marginBottom:10,boxSizing:"border-box"}}/>
            <input value={regWhatsapp} onChange={e=>setRegWhatsapp(e.target.value)}
              placeholder="WhatsApp 号码（可选）"
              style={{width:"100%",padding:"10px 12px",border:"1px solid #e0e0e0",borderRadius:8,
                fontSize:13,marginBottom:16,boxSizing:"border-box"}}/>
            {regErr && <div style={{color:"#e53e3e",fontSize:12,marginBottom:12}}>{regErr}</div>}
            <PaymentInfo ev={{donation_type:regModal.donationType,title_zh:regModal.eventTitle,fee_amount:null}}/>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>{setRegModal(null);setRegWechat("");setRegWhatsapp("");setRegErr("");}}
                style={{flex:1,padding:"10px",borderRadius:8,border:"1px solid #e0e0e0",
                  background:"#fff",cursor:"pointer",fontSize:13}}>
                取消
              </button>
              <button disabled={regLoading} onClick={async()=>{
                setRegLoading(true); setRegErr("");
                try {
                  const tok = token || localStorage.getItem("vajra_token") || "";
                  const regBody = {wechat:regWechat||null,whatsapp:regWhatsapp||null};
                  const r = await fetch(`${API}/vajra/events/${regModal.eventId}/register`,{
                    method:"POST",
                    headers:{"Authorization":`Bearer ${tok}`,"Content-Type":"application/json"},
                    body:JSON.stringify(regBody)
                  });
                  const d = await r.json();
                  if(!r.ok){setRegErr(d.detail||"报名失败");setRegLoading(false);return;}
                  if(regModal.donationType!=="free"){
                    const cr = await fetch(`${API}/vajra/events/${regModal.eventId}/checkout`,{
                      method:"POST",
                      headers:{"Authorization":`Bearer ${tok}`}
                    });
                    const cd = await cr.json();
                    if(cd.url){window.location.href=cd.url;return;}
                  }
                  setRegModal(null);setRegWechat("");setRegWhatsapp("");
                  load();
                }catch{setRegErr("网络错误，请重试");}
                setRegLoading(false);
              }} style={{flex:2,padding:"10px",borderRadius:8,border:"none",
                background:"linear-gradient(135deg,#6B1A1A,#B8860B)",color:"#fff",
                cursor:"pointer",fontSize:13,fontWeight:500,opacity:regLoading?0.7:1}}>
                {regLoading?"处理中…":(regModal.donationType==="free"?"确认报名":"报名并随喜")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event detail modal */}
      {selectedEvent && <EventDetailModal event={selectedEvent} onClose={()=>setSelectedEvent(null)} lang="zh" isLoggedIn={true} onRegister={(info)=>{ setSelectedEvent(null); setRegModal(info); }}/>}
    </div>
  );
}
